import dayjs from 'dayjs';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  DEFAULT_ADULT,
  DEFAULT_CHILDREN_AGES,
  DEFAULT_ROOMS,
} from '@travel/traveler-core/constants';
import { useDeviceTypes, useDialogHandler } from '@travel/traveler-core/hooks';
import { FlatButton, Popup } from '@travel/ui';
import { cx } from '@travel/utils';

import type { Props as DateInputProps } from 'components/DateInput';
import DateInput from 'components/DateInput';
import type {
  InternalSalesPromotionFormQuery,
  SalesPromotionFormQuery,
} from 'components/SalesPromotionInput';

import { getIsFetching } from 'store/bookingStep/selectors';
import { getMember } from 'store/member/selectors';
import { getIsDirectContractType } from 'store/providerInformation/selectors';

import { Translate } from 'core/translate';
import useFetchVacancyCalendarData from 'hooks/useFetchVacancyCalendarData';
import type { RoomFormQuery } from 'ProviderList-Types';

import NoAccountNeededPopup from '../NoAccountNeededPopup';

import styles from './reserveButton.module.scss';

export type Props = {
  wrapperClassName?: string;
  className?: string;
  /** Callback to call when click reserve button (reserve action)  */
  onClick: () => void;
  /** Current search params */
  searchParam: RoomFormQuery & SalesPromotionFormQuery & InternalSalesPromotionFormQuery;
  /** Callback to be called wheh date is selected */
  onSelectDate?: (startDate?: string, endDate?: string) => void;
  /** should show Vacancy Calendar component */
  shouldShowVacancyCalendar?: boolean;
  /** itemRatePlanId from card */
  itemRatePlanId?: string;
  isRakutenMembership?: boolean;
};

function ReserveButton(props: Props) {
  const {
    searchParam,
    onSelectDate,
    shouldShowVacancyCalendar = false,
    itemRatePlanId,
    isRakutenMembership,
  } = props;

  const { adults, childrenAges, noOfUnits, startDate, endDate, salesPromotionId } = searchParam;

  const isFetching = useSelector(getIsFetching);
  const { isMember: isLogin } = useSelector(getMember);
  const { isOpen, onOpen, onClose } = useDialogHandler(false);
  const {
    isOpen: isOpenCheckAvailability,
    onOpen: onOpenCheckAvailability,
    onClose: onCloseCheckAvailability,
  } = useDialogHandler(false);
  const { isPC } = useDeviceTypes();
  const shouldShowNoAccountNeededPopup = isPC && !isRakutenMembership && !isLogin;

  const { onClickFetchVacancyCalendar: onClickFetch } = useFetchVacancyCalendarData();
  const isDirectContractType = useSelector(getIsDirectContractType);

  const startMonth = dayjs(startDate).format('YYYY-MM');
  const nextMonth = dayjs(startDate)
    .add(1, 'month')
    .format('YYYY-MM');

  const onClickFetchVacancyCalendar = () => {
    if (shouldShowVacancyCalendar) {
      onClickFetch(
        Number(adults) || DEFAULT_ADULT,
        childrenAges ? childrenAges?.split(',').map(Number) : DEFAULT_CHILDREN_AGES,
        Number(noOfUnits) || DEFAULT_ROOMS,
        startDate,
        endDate,
        [startMonth, nextMonth],
        itemRatePlanId,
        salesPromotionId,
      );
    }
  };

  const customDatePickerProps: DateInputProps['customDatePickerProps'] = {
    dialogTitleString: <Translate id="Provider_Info.Vacancy_Calendar.Title" />,
    calendarBodyClass: styles.calendarBodyWrapper,
  };

  return (
    <div className={cx(props.wrapperClassName, styles.reserveButtonWrapper)}>
      <FlatButton
        className={cx(props.className, styles.reserveButton)}
        ariaLabel="Reserve"
        onClick={props.onClick}
        isSmallerButtonByDefault={true}
        isLoading={isFetching}
        data-testid="reservation-button"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
      >
        {!isLogin && isRakutenMembership ? (
          <Translate id="Provider_Info.Room_List.Sign_In_Up_To_Reserve" />
        ) : (
          <Translate id="Provider_Info.Room_List.Plan_Info.Reserve" />
        )}
      </FlatButton>

      {shouldShowNoAccountNeededPopup ? <NoAccountNeededPopup isOpen={isOpen} /> : null}

      {shouldShowVacancyCalendar && isDirectContractType && (
        <DateInput
          className={cx(props.className, styles.dateInputButton, styles.datedDateInputButton)}
          onPage="vacancyCalendar"
          onDateChanged={onSelectDate}
          onClickStartDateCallBack={onClickFetchVacancyCalendar}
          searchParam={searchParam}
          itemRatePlanId={itemRatePlanId}
          customDatePickerProps={customDatePickerProps}
          onDateButtonMouseEnter={onOpenCheckAvailability}
          onDateButtonMouseLeave={onCloseCheckAvailability}
        />
      )}

      {isPC && isOpenCheckAvailability ? (
        <Popup
          isOpen={isOpenCheckAvailability}
          onClose={() => {}}
          className={styles.messageWrapper}
        >
          <Translate id="Provider_Info.Plan_List.Room_Info.Check_Availability" />
        </Popup>
      ) : null}
    </div>
  );
}

export default ReserveButton;
